import { useField } from 'formik';
import { rgba } from 'polished';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { tokens } from '@treams/treams-ui';

import { TOTAL_NORM } from './norms';

const Container = styled(Flex)<{ hex: string }>`
	position: absolute;
	top: 6px;
	right: 6px;
	z-index: 1;
	align-items: center;
	background: white;
	border: 1px solid ${(props) => rgba(props.hex, 0.5)};
	border-radius: 4px;
	padding: 1px 5px;
	font-size: 13px;
	line-height: 18px;
	color: ${tokens.text.secondary};
`;

const Field = styled.input`
	width: 3ch;
	padding: 0;
	border: none;
	outline: none;
	background: transparent;
	text-align: right;
	font: inherit;
	color: inherit;

	// The spinner buttons don't fit in the corner of a cell.
	appearance: textfield;

	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		appearance: none;
		margin: 0;
	}
`;

export default function TalentReviewNorm(props: { name: string; hex: string }) {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<number>(props.name);

	// Draft holds in-progress input (empty string, leading zeros) so it is not
	// written into the form until it parses as a number. Blur drops the draft
	// and shows the last committed value.
	const [draft, setDraft] = useState<string>();

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const next = event.target.value;
		setDraft(next);

		const parsed = parseInt(next, 10);
		if (isNaN(parsed)) {
			return;
		}

		setValue(Math.max(0, Math.min(TOTAL_NORM, parsed)));
	};

	return (
		<Container hex={props.hex}>
			<Field
				type="number"
				min={0}
				max={TOTAL_NORM}
				step={1}
				inputMode="numeric"
				aria-label={t('settings.talentReviews.edit.norm.cell')}
				value={draft ?? value}
				onChange={handleChange}
				onBlur={() => setDraft(undefined)}
			/>
			%
		</Container>
	);
}
